import { Component, ChangeDetectionStrategy, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import {
  IdentityScoreData,
  ScoreFactor,
  ScoreSeverityTier,
} from '../../../../../../services/identity-security.service';

@Component({
  selector: 'app-score-details-dialog',
  standalone: true,
  imports: [CommonModule, RouterLink, DialogsModule],
  templateUrl: './score-details-dialog.component.html',
  styleUrl: './score-details-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScoreDetailsDialogComponent {
  scoreData = input.required<IdentityScoreData>();
  close = output<void>();
  /** Emitted when the customer reassigns a check's severity tier. */
  tierChange = output<{ checkId: string; severity: ScoreSeverityTier }>();

  public readonly severityTiers: ScoreSeverityTier[] = ['Critical', 'High', 'Medium', 'Low'];

  public onTierChange(checkId: string, severity: string): void {
    this.tierChange.emit({ checkId, severity: severity as ScoreSeverityTier });
  }

  public evaluatedCount = computed(() => {
    const s = this.scoreData();
    return s.passing_checks + s.failing_checks;
  });

  /** Failing and NOT accepted. These contribute to the score deduction. */
  public failingChecks = computed(() =>
    this.scoreData()
      .factors.filter((f) => f.status === 'fail' && !f.accepted)
      .sort((a, b) => b.score_impact - a.score_impact)
  );

  /** Failing but marked Accepted. No deduction, shown separately. */
  public acceptedChecks = computed(() =>
    this.scoreData().factors.filter((f) => f.status === 'fail' && f.accepted)
  );

  public passingChecks = computed(() =>
    this.scoreData().factors.filter((f) => f.status === 'pass')
  );

  public insufficientChecks = computed(() =>
    this.scoreData().factors.filter((f) => f.status === 'insufficient_data')
  );

  /**
   * Percentage points the score has lost, taken FROM the score rather than by adding up the
   * per-check impacts.
   *
   * The two are the same quantity but not the same number. `score_impact` is each check's share
   * rounded on its own; the pipeline rounds once at the end, over the evaluated weight. The sum
   * of the parts therefore drifts from the whole — measured over the ten-check catalog, 832 of
   * the 1023 possible failing combinations disagree, by up to 2 points.
   *
   * That drift is visible inside this very dialog: the badge in the title bar shows
   * `current_score` and this sentence sits directly beneath it. Deriving the sentence from the
   * score is what the ticket asks for, "generated using the Identity Risk Score Calculation",
   * and it makes the two impossible to contradict.
   *
   * `score_impact` is untouched and still orders the failing list below. It is the right number
   * for ranking, just not for a total.
   */
  public percentLost = computed(() => 100 - this.scoreData().current_score);

  /** Severity tiers in order, for the "weighted by severity" legend. */
  public tierOrder = computed(() =>
    (['Critical', 'High', 'Medium', 'Low'] as ScoreSeverityTier[]).filter(
      (tier) => this.scoreData().tier_weights[tier] != null
    )
  );

  /** Edge case from spec: every check disabled → score is meaningless. */
  public allDisabled = computed(() => {
    const s = this.scoreData();
    return s.disabled_checks > 0 && s.disabled_checks === s.total_checks;
  });

  /**
   * Why a check could not be evaluated. Two reasons, and only one of them is buyable.
   *
   * `required_license` is the discriminator the whole pipeline is built around: it carries a
   * tier when the tier is genuinely the reason, and is EMPTY when the source could not be read.
   * Six checks were changed on 2026-08-14 to keep it empty for a denied Graph endpoint, because
   * the alternative — showing "requires P1" to the 37 production organisations that already
   * hold P1 — sells them something that fixes nothing.
   *
   * The tier is named rather than written as the spec's generic "P1 or P2": the field says
   * which one, and a customer being asked to upgrade should be told to what.
   */
  public notEvaluatedReason(check: ScoreFactor): string {
    return check.required_license
      ? `Requires Microsoft Entra ID ${check.required_license} licensing`
      : 'Required Microsoft Graph permissions have not been configured';
  }

  public getSeverityClass(severity: string | null): string {
    const map: Record<string, string> = {
      Critical: 'severity-badge severity-critical',
      High: 'severity-badge severity-high',
      Medium: 'severity-badge severity-medium',
      Low: 'severity-badge severity-low',
    };
    return severity ? map[severity] ?? 'severity-badge severity-low' : 'severity-badge severity-low';
  }

  public onClose(): void {
    this.close.emit();
  }
}
